import { useEffect } from "react";
import { Sparkles } from "lucide-react";
import { Reveal, ChapterLabel } from "@/components/Reveal";
import { BOOKING_URL } from "@/lib/site";
import { useLang } from "@/i18n";

const ClassesPage = () => {
  const { t } = useLang();
  const d = t.classesPage;
  const signature = d.classes[d.classes.length - 1];

  useEffect(() => {
    window.__lenis ? window.__lenis.scrollTo(0, { immediate: true }) : window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="classes-page" className="bg-ivory">
      <section className="px-6 md:px-12 pt-40 md:pt-52 pb-20 md:pb-28">
        <div className="mx-auto max-w-[1600px]">
          <ChapterLabel index="01" title={d.label} />
          <Reveal delay={0.1}>
            <h1 data-testid="classes-page-headline"
              className="mt-10 font-display text-charcoal text-6xl sm:text-7xl lg:text-[7rem] leading-[0.95] tracking-tight">
              {d.h1}
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-md text-base md:text-lg leading-relaxed text-charcoal/65">{d.sub}</p>
          </Reveal>
        </div>
      </section>

      <section data-testid="signature-section" className="bg-espresso text-ivory px-6 md:px-12 py-24 md:py-36">
        <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-12 md:grid-cols-12 items-center">
          <div className="md:col-span-7">
            <Reveal>
              <div className="flex items-center gap-4">
                <Sparkles size={22} strokeWidth={1} className="text-gold" />
                <p className="label-caps text-ivory/50">{d.signatureLabel}</p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 data-testid="signature-headline"
                className="mt-8 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.0] tracking-tight">
                {signature.name}
              </h2>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-ivory/70">{signature.body[0]}</p>
            </Reveal>
          </div>
          <div className="md:col-span-4 md:col-start-9 flex flex-col items-start gap-6">
            <Reveal delay={0.2}><p className="label-caps text-gold">{signature.meta}</p></Reveal>
            <Reveal delay={0.26}>
              <a href={BOOKING_URL} data-testid="signature-book-button" className="btn-pill bg-ivory text-charcoal hover:bg-sand">
                {d.book}
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 py-24 md:py-40">
        <div className="mx-auto max-w-[1600px] flex flex-col gap-24 md:gap-36">
          {d.classes.map((c, i) => (
            <article key={c.name} data-testid={`class-detail-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-12 items-center">
              <div className={`md:col-span-5 ${i % 2 === 1 ? "md:order-2 md:col-start-8" : ""}`}>
                <Reveal y={40}>
                  <div className="img-frame aspect-[4/5]">
                    <img src={c.img} alt={c.alt} loading="lazy" className="h-full w-full object-cover" />
                  </div>
                </Reveal>
              </div>
              <div className={`md:col-span-6 ${i % 2 === 1 ? "md:order-1 md:col-start-1" : "md:col-start-7"}`}>
                <Reveal><p className="label-caps text-gold">{`0${i + 1}`}</p></Reveal>
                <Reveal delay={0.08}>
                  <h2 className="mt-4 font-display text-charcoal text-4xl sm:text-5xl leading-[1.0] tracking-tight">{c.name}</h2>
                </Reveal>
                {c.meta && <Reveal delay={0.14}><p className="mt-5 label-caps text-charcoal/50">{c.meta}</p></Reveal>}
                <Reveal delay={0.2}><div className="mt-6 space-y-4 text-base leading-relaxed text-charcoal/65">{c.body.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}</div></Reveal>
                <Reveal delay={0.26}>
                  <a href={BOOKING_URL} data-testid={`book-class-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className="btn-pill btn-primary mt-9">
                    {d.book}
                  </a>
                </Reveal>
              </div>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
};

export default ClassesPage;
